import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon, ClipboardIcon } from '@persona-id/design-tokens/icons';
import { sortBy } from 'lodash/fp';
import { ComponentProps, Fragment, ReactNode, RefObject, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import {
  change,
  Field,
  FormSection,
  formValueSelector,
  getFormSyncErrors,
  WrappedFieldProps,
} from 'redux-form';
import styled from 'styled-components';

import ConnectedFormSectionThemeSetConfig from 'dashboard/apps/InquiryTemplateEditor/forms/ConnectedFormSectionThemeSetConfig';
import FormSectionAutoclassificationConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionAutoclassificationConfig';
import FormSectionCaptureConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionCaptureConfig';
import FormSectionCountryConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionCountryConfig';
import FormSectionEnabledFileTypesConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionEnabledFileTypesConfig';
import FormSectionExpirationsConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionExpirationsConfig';
import FormSectionSessionsConfig from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionSessionsConfig';
import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import {
  FieldWrapper,
  SectionDescription,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import {
  CombinedStepForTree,
  CombinedStepType,
  LinkedWorkflowStep,
  PreviewPageMap,
  StepPreviewMap,
  VerificationTemplateVersionMap,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { buildScreenTreeAndGraph } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/utils';
import {
  AddStepConfig,
  FlowTreeFormValues,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import {
  getDedupedFieldSchema,
  getNewModuleConfig,
  stepsWorkflowsWithInsertedModule,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import { getDisplayNameForStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import AllowedHostsList from 'dashboard/components/AllowedHostsList';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormError from 'dashboard/components/FormControls/FormError';
import InputField from 'dashboard/components/FormControls/InputField';
import MultiSelectField from 'dashboard/components/FormControls/MultiSelectField';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import SelectField, { Option } from 'dashboard/components/FormControls/SelectField';
import TextAreaField from 'dashboard/components/FormControls/TextAreaField';
import ToggleSliderField from 'dashboard/components/FormControls/ToggleSliderField';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import { OptionType } from 'dashboard/components/ReactSelect';
import { MonoSpan } from 'dashboard/components/Typography';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import copyToClipboard from 'dashboard/lib/utils/copyToClipboard';
import { InquiryTemplateModule, ModuleOptionKey } from 'dashboard/models/inquiry-template-module';
import {
  InquiryTemplateVersion,
  InquiryTemplateVersionStep,
  InquiryTemplateVersionStepUi,
  isCombinedStepConfig,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import {
  GovernmentIdFeature,
  VerificationTemplateVersionDocument,
  VerificationTemplateVersionGovernmentId,
} from 'dashboard/models/verification-template-version';
import { RootState } from 'dashboard/redux/store';
import Banner from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Popover from 'design-system/components/Popover';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import { fadeIn } from 'design-system/variables/keyframes';
import { TransitionDuration } from 'design-system/variables/transitions';
import { INQUIRY_LANGUAGES_ENGLISH, InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { FieldSchema } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import {
  InputCountrySelectComponentConfig,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';
import { assertNever } from 'lib/utils/types';

const StyledBanner = styled(Banner)`
  padding: ${Spacing[2]} ${Spacing[2]};
`;

const CopyButton = styled(Button).attrs(() => ({
  type: 'button',
  variant: 'inverse',
  width: 'icon',
  size: 'sm',
}))`
  svg {
    width: 14px;
    height: 14px;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Spacing[1]};
  animation: ${fadeIn} ${TransitionDuration.Medium} ease-in-out;
  padding: 6px;
  margin: ${Spacing[1]};
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
`;

const SupportedLocalesContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]} ${Spacing[1]};
  justify-content: flex-start;
  flex-direction: row;
  flex-wrap: wrap;
  max-height: 200px;
`;

const StyledId = styled(MonoSpan).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-left: ${Spacing[2]};
  margin-right: ${Spacing[2]};
  display: inline-block;
  width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LANGUAGE_OPTIONS = Object.keys(INQUIRY_LANGUAGES_ENGLISH).map(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (languageCode: InquiryIso6391LanguageCode) => ({
    value: languageCode,
    label: INQUIRY_LANGUAGES_ENGLISH[languageCode],
  }),
);

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
  width: 100%;
  padding: 0 ${Spacing[5]};
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.secondary};
`;

const SORTED_LANGUAGE_OPTIONS =
  sortBy<OptionType<InquiryIso6391LanguageCode>>('label')(LANGUAGE_OPTIONS);

const CARD_DESCRIPTIONS: Record<CombinedStepType, string> = {
  [UiStepType.GovernmentId]: 'Enabled capture methods for Government ID submission',
  [UiStepType.Selfie]: 'Enabled capture methods for Selfie submission',
  [UiStepType.SelfieReflection]: 'Enabled capture methods for Selfie Reflection submission',
  [UiStepType.Document]: 'Accepted file types and enabled capture methods for Document submission',
};

const ValidatedCard = styled(Card)<{ isValid: boolean }>`
  ${({ isValid, theme }) =>
    !isValid &&
    `
    outline-style: solid;
    outline-width: 1px;
    outline-color: ${theme.color.variants.danger.default}
    `}
`;

type CountrySelectConfig = {
  stepIndex: number;
  step: InquiryTemplateVersionStepUi;
  componentIndex: number;
};

interface FormSectionGeneralConfigProps {
  inquiryTemplateVersion: InquiryTemplateVersion;
  basePath: string;
  currentLocale: InquiryIso6391LanguageCode;
  steps: InquiryTemplateVersionStep[];
  stepsPreviewMap: StepPreviewMap;
  previewPageMap: PreviewPageMap;
  extractedWorkflowSteps: LinkedWorkflowStep[];
  vtmplVersionMap: VerificationTemplateVersionMap;
  workflowVersionMap: WorkflowVersionMap;
  fieldSchema: FieldSchema[];
  handleSetLocale: (locale: InquiryIso6391LanguageCode) => void;
  handleLoadModule: (
    moduleKey: ModuleOptionKey,
    currentFieldSchema: FieldSchema[],
  ) => Promise<InquiryTemplateModule>;
  handleUpdateFormValues: ({
    steps,
    stepsPreview,
    workflowVersionMap,
    vtmplVersionUpdates,
    fieldSchema,
  }: FlowTreeFormValues) => void;
  title?: string;
  rightColumnRef?: RefObject<HTMLDivElement>;
}

const form = INQUIRY_TEMPLATE_EDITOR_FORM_NAME;
const selector = formValueSelector(form);

const FormSectionGeneralConfig = ({
  inquiryTemplateVersion,
  basePath,
  currentLocale,
  steps,
  stepsPreviewMap,
  previewPageMap,
  extractedWorkflowSteps,
  vtmplVersionMap,
  workflowVersionMap,
  fieldSchema,
  handleSetLocale,
  handleLoadModule,
  handleUpdateFormValues,
  title,
  rightColumnRef,
}: FormSectionGeneralConfigProps) => {
  const dispatch = useDispatch();
  const validationErrors = useSelector<RootState, any>((state) =>
    getFormSyncErrors(INQUIRY_TEMPLATE_EDITOR_FORM_NAME)(state),
  );
  const editableUngated = useIsEditableWithOptions();
  const hasAccessForFreeTier = useHasAccessForTierDeterminator()(EditorAccessTier.InquiriesFree);
  const hasAccessForBasicTier = useHasAccessForTierDeterminator()(EditorAccessTier.InquiriesBasic);
  const isITEInternal = useIsITEInternal();
  const isAutoclassificationFFEnabled = useFeatureFlag('inquiry:auto-classification');
  const isMaxSessionLimitConfigEnabled = useFeatureFlag(
    'dashboard:fle-enable-configure-inquiry-max-session-limit',
  );
  const showReusablePersonasFleToggle = useFeatureFlag(
    'dashboard:fle-enable-reusable-personas-toggle',
  );
  const reusablePersonasEnabled = useFeatureFlag('inquiry:enable-reusable-personas');

  const fieldSchemaOptions = useMemo(
    () => fieldSchema.map(({ label, key }) => ({ label: label ?? key, value: key })),
    [fieldSchema],
  );

  const supportedLocales = useSelector<RootState, InquiryIso6391LanguageCode[]>(
    (state) => selector(state, 'enabled-locales') ?? [],
  );

  const itmplId = inquiryTemplateVersion.relationships['inquiry-template']?.data?.id;
  const itmplVersionId = inquiryTemplateVersion.id;

  const [confirmationActive, { enable: showConfirmation, disable: hideConfirmation }] =
    useToggle(false);

  const copyTextToClipboard = async (text: string) => {
    const copyWasSuccessful = await copyToClipboard(text);
    if (copyWasSuccessful) {
      showConfirmation();
      setTimeout(() => {
        hideConfirmation();
      }, 1200);
    }
  };

  const isDeviceHandoffEnabled = steps.filter(isCombinedStepConfig).some((step) => {
    const {
      'enabled-capture-options-desktop': captureOptionsDesktop,
      'enabled-capture-options-mobile': captureOptionsMobile,
    } = step.config;
    return [...captureOptionsDesktop, ...captureOptionsMobile].includes('device_handoff');
  });

  const selectedSupportedLocales = useMemo<Set<InquiryIso6391LanguageCode>>(
    () => new Set(supportedLocales),
    [supportedLocales],
  );

  useEffect(() => {
    if (!selectedSupportedLocales.has(currentLocale)) {
      handleSetLocale(supportedLocales[0]);
    }
  }, [selectedSupportedLocales, currentLocale, handleSetLocale, supportedLocales]);

  const supportedLocalesLabelTextMapper = useLabelTextMapper(
    LANGUAGE_OPTIONS,
    'Languages Supported',
    {
      emptyIsAll: false,
    },
  );

  const isThemeSetFrontendEnabled = useFeatureFlag('dashboard:fle-theme-set-index-and-editor');

  const configCards = useMemo(() => {
    const configCards: ReactNode[] = [
      <Card key='template'>
        <Card.Body>
          <Card.Title>
            Template
            <StyledId>{itmplId}</StyledId>
            <div>
              {/*Anchor the popover over a zero-width div aligned horizontally with the below button*/}
              <Popover open={confirmationActive} trigger={<div style={{ margin: '0 auto' }} />}>
                <Popover.Content side='top' align='center' style={{ zIndex: 2 }}>
                  <StyledCard elevated>
                    <Paragraph>Copied</Paragraph>
                  </StyledCard>
                </Popover.Content>
              </Popover>
              <DropdownMenu
                trigger={
                  <CopyButton aria-label='Copy'>
                    <ClipboardIcon />
                  </CopyButton>
                }
              >
                <DropdownMenu.Item
                  id='copy-token__inquiry-template'
                  aria-label='Copy Inquiry Template ID'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    copyTextToClipboard(itmplId);
                  }}
                >
                  Copy Inquiry Template ID
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  id='copy-token__inquiry-template-version'
                  aria-label='Copy Inquiry Template Version ID'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    copyTextToClipboard(itmplVersionId);
                  }}
                >
                  Copy Inquiry Template Version ID
                </DropdownMenu.Item>
              </DropdownMenu>
            </div>
          </Card.Title>
          <Spacer height={0.5} />
          <FieldWrapper>
            <Field
              size='md'
              type='text'
              id='template-name'
              name='template-name'
              label={<span>Template name</span>}
              data-cy='form-general-name__field-input'
              placeholder='Template name'
              component={InputField}
              // enable for all customer tiers with template write permissions
              disabled={!editableUngated}
            />
          </FieldWrapper>
          <Spacer height={0.5} />
          <FieldWrapper>
            <Field
              size='md'
              type='text'
              id='template-description'
              name='template-description'
              label={<span>Template description</span>}
              placeholder='Add a description...'
              component={TextAreaField}
              // enable for all customer tiers with template write permissions
              disabled={!editableUngated}
            />
          </FieldWrapper>
        </Card.Body>
      </Card>,
      isThemeSetFrontendEnabled ? (
        <>
          <FieldWrapper>
            <Field
              id='themeSetId'
              name='themeSetId'
              component={ConnectedFormSectionThemeSetConfig}
            />
          </FieldWrapper>
          <Spacer height={2} />
        </>
      ) : null,
      <Card key='template-enabled-locales'>
        <Card.Body>
          <Card.Title>Supported languages</Card.Title>
          <SectionDescription>
            Internationalize your inquiry flow by selecting which languages are available in the
            language selector.
            <Link to='/dashboard/contact-us'> Missing a language? Contact us</Link>
          </SectionDescription>
          <FieldWrapper>
            <Field
              id='enabled-locales'
              name='enabled-locales'
              placeholder='Select supported languages...'
              labelTextMapper={supportedLocalesLabelTextMapper}
              component={MultiSelectField}
              selectOptionValues={SORTED_LANGUAGE_OPTIONS}
              selectAllEnabled
              searchEnabled
              // enable for all customer tiers with template write permissions
              disabled={!editableUngated}
            />
          </FieldWrapper>
          <Spacer height={1} />
          {supportedLocales.length !== 0 && (
            <SupportedLocalesContainer>
              {SORTED_LANGUAGE_OPTIONS.filter(({ value }) =>
                selectedSupportedLocales.has(value),
              ).map(({ label, value }) => (
                <Sticker key={value} variant='primary'>
                  {label}
                </Sticker>
              ))}
            </SupportedLocalesContainer>
          )}
        </Card.Body>
      </Card>,
    ];

    const countrySelectConfigs: CountrySelectConfig[] = steps.reduce((configs, step, stepIndex) => {
      if (isUiStepConfig(step)) {
        const componentIndex = step.config.components.findIndex(
          (component) => component.type === UiComponentConfigType.InputCountrySelect,
        );
        return componentIndex !== -1
          ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            configs.concat({ stepIndex, step, componentIndex })
          : configs;
      } else {
        return configs;
      }
    }, []);

    countrySelectConfigs.forEach(({ stepIndex, step, componentIndex }, index) => {
      const title =
        countrySelectConfigs.length > 1
          ? `Country select ${index + 1} options`
          : 'Country select options';
      const countrySelectComponent = step.config.components[
        componentIndex
      ] as InputCountrySelectComponentConfig;
      const { 'country-codes': countryCodes, field } = countrySelectComponent.config;
      configCards.push(
        <Card key={step.name}>
          <Card.Body>
            <Card.Title>
              <HorizontalContainer>
                {title}
                {!hasAccessForFreeTier && (
                  <ProductUpsellTooltip
                    side='top'
                    learnMoreId='country-select-config'
                    accessTier={EditorAccessTier.InquiriesFree}
                    trigger={
                      <div>
                        <InquiriesGatingSticker
                          dataMarketingIdSuffix='country-select-config'
                          stickerType={EnhancedGatingStickerType.Limited}
                        />
                      </div>
                    }
                  />
                )}
              </HorizontalContainer>
            </Card.Title>
            <SectionDescription>
              Countries that users can select from the dropdown
            </SectionDescription>
            <FormSectionCountryConfig
              name={`steps.${stepIndex}.config.components.${componentIndex}.config`}
              fieldPath={`${basePath}/config/fields/${field}`}
              selectedCountries={countryCodes}
            />
          </Card.Body>
        </Card>,
      );
    });

    steps.forEach((step, idx) => {
      switch (step.type) {
        case UiStepType.Document: {
          const vtmplVersionId = step.config['verification-template-version-id'];
          const isValid =
            validationErrors['steps']?.[idx] == null &&
            validationErrors['verificationTemplateVersions']?.[`${vtmplVersionId}`] == null;
          const enabledDesktopCaptureOptions = step.config['enabled-capture-options-desktop'];
          const enabledMobileCaptureOptions = step.config['enabled-capture-options-mobile'];
          const removeCaptureFromCaptureConfig = () => {
            const filteredDesktopCaptureOptions = enabledDesktopCaptureOptions.filter(
              (option) => option !== 'web_camera',
            );
            const filteredMobileCaptureOptions = enabledMobileCaptureOptions.filter(
              (option) => option !== 'web_camera' && option !== 'mobile_camera',
            );
            dispatch(
              change(
                form,
                `steps.${idx}.config.enabled-capture-options-desktop`,
                filteredDesktopCaptureOptions,
              ),
            );
            dispatch(
              change(
                form,
                `steps.${idx}.config.enabled-capture-options-mobile`,
                filteredMobileCaptureOptions,
              ),
            );
          };

          configCards.push(
            <ValidatedCard isValid={isValid} key={step.name}>
              <Card.Body>
                <Card.Title>{`Image capture methods: ${getDisplayNameForStep(step)}`}</Card.Title>
                <SectionDescription>{CARD_DESCRIPTIONS[step.type]}</SectionDescription>
                <Label size='lg' weight='bold'>
                  Accepted File Types
                </Label>
                <FormSectionEnabledFileTypesConfig
                  name={`verificationTemplateVersions.${vtmplVersionId}.attributes`}
                  removeCaptureFromCaptureConfig={removeCaptureFromCaptureConfig}
                />
                <Label
                  size='lg'
                  style={{ margin: `${Spacing[2]} 0 ${Spacing[4]} 0` }}
                  weight='bold'
                >
                  Capture Methods
                </Label>
                <FormSectionCaptureConfig
                  step={step}
                  name={`steps.${idx}.config`}
                  enabledFileTypes={
                    (vtmplVersionMap[vtmplVersionId] as VerificationTemplateVersionDocument)
                      .attributes['enabled-file-types']
                  }
                />
              </Card.Body>
            </ValidatedCard>,
          );
          break;
        }
        case UiStepType.Selfie: {
          const isValid = validationErrors['steps']?.[idx] == null;
          configCards.push(
            <ValidatedCard isValid={isValid} key={step.name}>
              <Card.Body>
                <Card.Title>{`Image capture methods: ${getDisplayNameForStep(step)}`}</Card.Title>
                <SectionDescription>{`${CARD_DESCRIPTIONS[step.type]} (${
                  step.config['verification-template-version-id']
                })`}</SectionDescription>
                <FormSectionCaptureConfig step={step} name={`steps.${idx}.config`} />
              </Card.Body>
            </ValidatedCard>,
          );
          break;
        }
        case UiStepType.GovernmentId: {
          const vtmplVersionId = step.config['verification-template-version-id'];
          const isValid =
            validationErrors['steps']?.[idx] == null &&
            validationErrors['verificationTemplateVersions']?.[`${vtmplVersionId}`] == null;
          const enabledDesktopCaptureOptions = step.config['enabled-capture-options-desktop'];
          const enabledMobileCaptureOptions = step.config['enabled-capture-options-mobile'];
          const removeCaptureFromCaptureConfig = () => {
            const filteredDesktopCaptureOptions = enabledDesktopCaptureOptions.filter(
              (option) => option !== 'web_camera',
            );
            const filteredMobileCaptureOptions = enabledMobileCaptureOptions.filter(
              (option) => option !== 'web_camera' && option !== 'mobile_camera',
            );

            dispatch(
              change(
                form,
                `steps.${idx}.config.enabled-capture-options-desktop`,
                filteredDesktopCaptureOptions,
              ),
            );
            dispatch(
              change(
                form,
                `steps.${idx}.config.enabled-capture-options-mobile`,
                filteredMobileCaptureOptions,
              ),
            );
          };
          const setGovIdVtvAutoclassificationEnabledFeatures = (
            isAutoclassificationOn: boolean,
          ) => {
            const vtvEnabledFeatures = (
              vtmplVersionMap[vtmplVersionId] as VerificationTemplateVersionGovernmentId
            ).attributes['enabled-features'];
            const newVtvEnabledFeatures = (() => {
              if (isAutoclassificationOn) {
                return vtvEnabledFeatures?.includes(GovernmentIdFeature.Autoclassification)
                  ? vtvEnabledFeatures
                  : [...vtvEnabledFeatures, GovernmentIdFeature.Autoclassification];
              } else {
                return vtvEnabledFeatures?.filter(
                  (feature) => feature !== GovernmentIdFeature.Autoclassification,
                );
              }
            })();

            dispatch(
              change(
                form,
                `verificationTemplateVersions.${vtmplVersionId}.attributes.enabled-features`,
                newVtvEnabledFeatures,
              ),
            );
          };
          const setFieldKeyCountry = (fieldKeyCountry: string | null) => {
            dispatch(change(form, `steps.${idx}.config.field-key-country`, fieldKeyCountry));
          };
          // recursive function to find the first occurence of gov id step - it could be hidden behind branches
          const findPrevAndNextStep = (
            currentStepsTree: CombinedStepForTree[],
            parentStep?: CombinedStepForTree,
            branchIndex?: number,
          ): [
            CombinedStepForTree | undefined,
            CombinedStepForTree | undefined,
            number | undefined,
          ] => {
            const index = currentStepsTree.findIndex(
              (stepForTree) =>
                stepForTree.type === UiStepType.GovernmentId && stepForTree.name === step.name,
            );
            if (index === -1) {
              // the gov id step is likely hidden in a branch
              for (const stepForTree of currentStepsTree) {
                if (Array.isArray(stepForTree.branches)) {
                  for (let index = 0; index < stepForTree.branches.length; ++index) {
                    const [prevStep, nextStep, calculatedBranchIndex] = findPrevAndNextStep(
                      stepForTree.branches[index],
                      stepForTree,
                      index,
                    );
                    // if we found the gov id step, early return here
                    if (prevStep != null && nextStep != null) {
                      return [prevStep, nextStep, calculatedBranchIndex];
                    }
                  }
                }
              }
              return [undefined, undefined, undefined];
            } else {
              const nextStep = currentStepsTree[index];
              // the found step is the first element in the branch, so the previous step should be the parent step with branching
              const prevStep = index === 0 ? parentStep : currentStepsTree[index - 1];
              const calculatedBranchIndex = index === 0 ? branchIndex : undefined;
              return [prevStep as CombinedStepForTree, nextStep, calculatedBranchIndex];
            }
          };
          const prependCountrySelectStep = async () => {
            // add a country select step before the gov id step
            // this is a lighter version of handleInsertModule in InquiryTemplateFlowEditor
            const [stepsTree, _] = buildScreenTreeAndGraph({
              steps,
              stepsPreviewMap,
              previewPageMap,
              extractedWorkflowSteps,
              isITEInternal,
            });
            // find the first occurence in the tree of the gov id step, and the step prior to it
            const [prevStep, nextStep, branchIndex] = findPrevAndNextStep(stepsTree);
            if (prevStep == null || nextStep == null) {
              captureMessage('Failed to prepend country select screen.', 'warning', {
                prevStep: prevStep,
                nextStep: nextStep,
                branchIndex: branchIndex,
              });
              return;
            }
            const moduleAddStepConfig: AddStepConfig = {
              prevStep: prevStep,
              nextStep: nextStep,
              branchIndex: branchIndex,
            };
            const newModule = await handleLoadModule(ModuleOptionKey.CountrySelect, fieldSchema);
            const { 'field-schemas': moduleFieldSchema, screens: moduleScreens } =
              newModule.attributes;
            if (moduleScreens) {
              const stepsPreview = Object.values(stepsPreviewMap);
              const updatedFieldSchema = moduleFieldSchema.length
                ? getDedupedFieldSchema(fieldSchema, moduleFieldSchema)
                : null;
              const { updatedSteps, updatedStepsPreview, updatedWorkflowMap } =
                stepsWorkflowsWithInsertedModule(
                  {
                    steps,
                    stepsPreview,
                    workflowVersionMap,
                  },
                  getNewModuleConfig(newModule.attributes, moduleAddStepConfig),
                  moduleAddStepConfig,
                );

              handleUpdateFormValues({
                steps: updatedSteps,
                stepsPreview: updatedStepsPreview,
                workflowVersionMap: updatedWorkflowMap,
                fieldSchema: updatedFieldSchema,
              });
              // Update the gov id field key country to reference the newly added input country select
              // safeguard check that selected_country_code exists
              const fieldKey = updatedFieldSchema?.find(
                (fieldSchema) => fieldSchema.key === 'selected_country_code',
              );
              if (fieldKey && updatedSteps) {
                setFieldKeyCountry(fieldKey.key);
              }
            }
          };
          configCards.push(
            <ValidatedCard isValid={isValid} key={step.name}>
              <Card.Body>
                <Card.Title>{`Image capture methods: ${getDisplayNameForStep(step)}`}</Card.Title>
                <SectionDescription>{`${CARD_DESCRIPTIONS[step.type]} (${
                  step.config['verification-template-version-id']
                })`}</SectionDescription>
                <Label size='lg' weight='bold'>
                  Accepted File Types
                </Label>
                <FormSectionEnabledFileTypesConfig
                  name={`verificationTemplateVersions.${vtmplVersionId}.attributes`}
                  removeCaptureFromCaptureConfig={removeCaptureFromCaptureConfig}
                />
                <Label
                  size='lg'
                  style={{ margin: `${Spacing[2]} 0 ${Spacing[4]} 0` }}
                  weight='bold'
                >
                  Capture Methods
                </Label>
                <FormSectionCaptureConfig step={step} name={`steps.${idx}.config`} />
                {isAutoclassificationFFEnabled && (
                  <>
                    <Label size='lg' style={{ margin: `${Spacing[4]} 0 0 0` }} weight='bold'>
                      Additional
                    </Label>
                    <FormSectionAutoclassificationConfig
                      name={`steps.${idx}.config`}
                      isAutoclassificationOn={
                        step.config['auto-classification-config']?.['is-enabled']
                      }
                      setGovIdVtvAutoclassificationEnabledFeatures={
                        setGovIdVtvAutoclassificationEnabledFeatures
                      }
                      prependCountrySelectStep={prependCountrySelectStep}
                      currentFieldKeyCountry={step.config['field-key-country']}
                      setFieldKeyCountry={setFieldKeyCountry}
                      fieldSchema={fieldSchema}
                    />
                  </>
                )}
              </Card.Body>
            </ValidatedCard>,
          );
          break;
        }
        case UiStepType.Complete:
        case UiStepType.Integration:
        case UiStepType.VerifyWithPersona:
        case UiStepType.Ui:
        case UiStepType.SelfieReflection:
        case UiStepType.Loop:
          break;
        default:
          assertNever(step);
      }
    });
    configCards.push(
      <Card key='device-handoff-config'>
        <Card.Body>
          <Card.Title>Device handoff configuration</Card.Title>
          <SectionDescription>
            {isDeviceHandoffEnabled
              ? 'Customize device handoff behaviour, if enabled for Image capture methods'
              : 'Device handoff is not currently enabled'}
          </SectionDescription>
          <FieldWrapper>
            <Field
              component={ToggleSliderField}
              label={
                <span>
                  Allow device handback
                  <HelpIcon>
                    <p>Allows the user to return to their original device following handoff.</p>
                    <br />
                    <p>
                      After device handoff is completed, the original device will render a button
                      that, when clicked, allows the user to resume the inquiry on the original
                      device, and invalidate child sessions.
                    </p>
                  </HelpIcon>
                </span>
              }
              labelOn='Enabled'
              labelOff='Disabled'
              name='enable-device-handback'
              id='enable-device-handback'
              disabled={!editableUngated || !isDeviceHandoffEnabled}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Field
              component={ReactSelectField}
              options={fieldSchemaOptions}
              label={
                <span>
                  Email field key
                  <HelpIcon>
                    <p>
                      This populates the specified field with the email address used during device
                      handoff if the specified field is empty, and prefills the device handoff input
                      value with the field value if the specified field is not empty.
                    </p>
                  </HelpIcon>
                </span>
              }
              name='field-key-device-handoff-email'
              id='field-key-device-handoff-email'
              disabled={!editableUngated || !isDeviceHandoffEnabled}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Field
              component={ReactSelectField}
              options={fieldSchemaOptions}
              label={
                <span>
                  Phone number field key
                  <HelpIcon>
                    <p>
                      This populates the specified field with the phone number used during device
                      handoff if the specified field is empty, and prefills the device handoff input
                      value with the field value if the specified field is not empty.
                    </p>
                  </HelpIcon>
                </span>
              }
              name='field-key-device-handoff-phone-number'
              id='field-key-device-handoff-phone-number'
              disabled={!editableUngated || !isDeviceHandoffEnabled}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Field
              component={ToggleSliderField}
              label={
                <span>
                  Disable device handoff inputs if prefilled
                  <HelpIcon>
                    <p>
                      When enabled, users will not be able to modify the email address or phone
                      number used for device handoff if values have been prefilled for the
                      corresponding fields.
                    </p>
                    <br />
                    <p>
                      This configuration will do nothing if device handoff field keys are not
                      configured above.
                    </p>
                  </HelpIcon>
                </span>
              }
              labelOn='Enabled'
              labelOff='Disabled'
              name='disable-device-handoff-inputs-if-prefilled'
              id='disable-device-handoff-inputs-if-prefilled'
              disabled={!editableUngated || !isDeviceHandoffEnabled}
            />
          </FieldWrapper>
        </Card.Body>
      </Card>,
    );

    const expirationDescription =
      'Time interval (in minutes) after which an Inquiry expires and the flow can no longer be accessed. \
Separate expiration intervals can be configured from Inquiry creation, start, resume, and one time links. \
\nLonger intervals increase the risk of sensitive user information being inappropriately accessed or hijacked.';

    configCards.push(
      <Card key='expiration-intervals'>
        <Card.Body>
          <Card.Title>
            <HorizontalContainer>
              Expiration intervals
              {!hasAccessForBasicTier && (
                <ProductUpsellTooltip
                  side='top'
                  accessTier={EditorAccessTier.InquiriesBasic}
                  learnMoreId='expiration-intervals'
                  trigger={
                    <div>
                      <InquiriesGatingSticker
                        dataMarketingIdSuffix='expiration-intervals'
                        stickerType={EnhancedGatingStickerType.Limited}
                      />
                    </div>
                  }
                />
              )}
            </HorizontalContainer>
          </Card.Title>
          <SectionDescription>{expirationDescription}</SectionDescription>
          <FormSectionExpirationsConfig />
        </Card.Body>
      </Card>,
    );

    if (isMaxSessionLimitConfigEnabled) {
      configCards.push(
        <Card key='inquiry-sessions'>
          <Card.Body>
            <Card.Title>
              <HorizontalContainer>Inquiry Sessions</HorizontalContainer>
            </Card.Title>
            <FormSectionSessionsConfig
              initialMaxSessionLimit={inquiryTemplateVersion.attributes['max-session-limit']}
            />
          </Card.Body>
        </Card>,
      );
    }

    configCards.push(
      <Card key='redirect-config'>
        <Card.Body>
          <Card.Title>
            <HorizontalContainer>Redirect configuration</HorizontalContainer>
          </Card.Title>
          <SectionDescription>
            Configure the handoff from the Persona flow back to your application. For more
            information, see our documentation on{' '}
            <a
              href='https://docs.withpersona.com/docs/hosted-flow-redirecting-on-complete'
              target='_blank'
              rel='noreferrer'
            >
              Redirecting On Complete
            </a>
            .
          </SectionDescription>

          <FieldWrapper>
            <Field
              // NOTE: We don't validate this field because we actually support
              // a very broad range of URIs, such as custom mobile intents.
              id='redirect-uri'
              name='redirect-uri'
              label={
                <span>
                  Default Redirect URI
                  <HelpIcon>
                    This redirect URI will be used for all Inquiries created from this Template.
                    Redirect URI can also be specified on a per-Inquiry basis.
                  </HelpIcon>
                </span>
              }
              size='md'
              type='text'
              data-test='form-general-redirect-config__redirect-uri'
              component={InputField}
              // enable for all customer tiers with template write permissions
              disabled={!editableUngated}
              placeholder='https://'
            />
          </FieldWrapper>

          <Spacer height={0.5} />

          <FormSection name='redirect-query-string-config' component={Fragment}>
            <Field
              component={ToggleSliderField}
              label='Include inquiry ID'
              labelOn='Enabled'
              labelOff='Disabled'
              name='include-inquiry-id'
              id='form-section-redirect-query-string-config__include-inquiry-id'
              data-test='form-section-redirect-query-string-config__include-inquiry-id'
              disabled={!editableUngated}
            />
            <Field
              component={ToggleSliderField}
              label='Include reference ID'
              labelOn='Enabled'
              labelOff='Disabled'
              name='include-reference-id'
              id='form-section-redirect-query-string-config__include-reference-id'
              data-test='form-section-redirect-query-string-config__include-reference-id'
              disabled={!editableUngated}
            />
            <Field
              component={ToggleSliderField}
              label='Include status'
              labelOn='Enabled'
              labelOff='Disabled'
              name='include-status'
              id='form-section-redirect-query-string-config__include-status'
              data-test='form-section-redirect-query-string-config__include-status'
              disabled={!editableUngated}
            />
            <Field
              component={SelectField}
              label={
                <span>
                  Include fields
                  <HelpIcon>
                    <p>
                      Use in conjunction with &lsquo;Field keys&rsquo; to control what fields are
                      included in the redirect query string. If blank, defaults to
                      &lsquo;Blocklist&rsquo;.
                    </p>
                    <br />
                    <p>
                      To send all fields, select &lsquo;Blocklist&rsquo; and leave field keys blank.
                      To not send any fields, select &lsquo;Allowlist&rsquo; and leave field keys
                      blank.
                    </p>
                  </HelpIcon>
                </span>
              }
              name='include-fields'
              id='form-section-redirect-query-string-config__include-fields'
              data-test='form-section-redirect-query-string-config__include-fields'
              disabled={!editableUngated}
              placeholder='Select...'
            >
              <Option value='blocklist'>Blocklist</Option>
              <Option value='allowlist'>Allowlist</Option>
            </Field>
            <Field
              component={ReactSelectField}
              options={fieldSchemaOptions}
              label={
                <span>
                  Field keys
                  <HelpIcon>
                    Warning: adding new fields to the template will not cause selected values to
                    update. Please ensure that newly added fields are properly allowlisted or
                    blocklisted.
                  </HelpIcon>
                </span>
              }
              name='field-keys'
              id='form-section-redirect-query-string-config__field-keys'
              data-test='form-section-redirect-query-string-config__field-keys'
              disabled={!editableUngated}
              isMulti
            />
          </FormSection>
        </Card.Body>
      </Card>,
    );

    const { 'has-custom-reusable-persona-config': hasCustomReusablePersonaConfig } =
      inquiryTemplateVersion.attributes;

    if (reusablePersonasEnabled && showReusablePersonasFleToggle) {
      configCards.push(
        <Card key='reusable-persona-config'>
          <Card.Body>
            <Card.Title>
              <HorizontalContainer>Reusable Personas</HorizontalContainer>
            </Card.Title>

            {hasCustomReusablePersonaConfig && (
              <StyledBanner variant='warning'>
                <CircleInfoIcon />
                <Paragraph>
                  If you wish to make changes, please{' '}
                  <Link to='/dashboard/contact-us' target='_blank' rel='noreferrer'>
                    contact support
                  </Link>{' '}
                  or your account team.
                </Paragraph>
              </StyledBanner>
            )}
            <FieldWrapper>
              <Field
                id='has-reusable-persona-config'
                name='has-reusable-persona-config'
                data-test='has-reusable-persona-config'
                label={
                  <>
                    <span>Enable Reusable Personas?</span>

                    <Paragraph>
                      To learn more about Reusable Personas you can view our documentation{' '}
                      <a
                        href='https://help.withpersona.com/articles/4DXUhrP7e1sLBj2SKIDsMX/'
                        target='_blank'
                        rel='noreferrer'
                      >
                        here
                      </a>
                      .
                    </Paragraph>
                  </>
                }
                labelOn='Enabled'
                labelOff='Disabled'
                component={ToggleSliderField}
                disabled={!editableUngated || hasCustomReusablePersonaConfig}
              />
            </FieldWrapper>
          </Card.Body>
        </Card>,
      );
    }

    configCards.push(
      <Card key='security-config'>
        <Card.Body>
          <Card.Title>
            <HorizontalContainer>Security</HorizontalContainer>
          </Card.Title>

          <FieldWrapper>
            <Field
              id='allowed-hosts'
              name='allowed-hosts'
              data-test='allowed-hosts'
              label={
                <>
                  <span>
                    Allowed Domains
                    <HelpIcon>
                      <p>
                        This allowlist controls which domains are able to embed your flow via
                        Embedded Flow, and what URLs are valid redirect targets after completion of
                        a Hosted Flow. A wildcard character (*) can be used to specify subdomains.
                      </p>
                      <br />
                      <p>
                        If this list is empty, all domains are allowed. Otherwise, Persona will only
                        allow redirects or embeds for domains in this list.
                      </p>
                    </HelpIcon>
                  </span>

                  <Paragraph>
                    The domain allowlist overrides the global allowlist set in the{' '}
                    <Link to='/dashboard/inquiry-manage-domain' target='_blank' rel='noreferrer'>
                      Inquiry Domain Manager
                    </Link>
                    .
                  </Paragraph>
                </>
              }
              component={AllowedHostsListField}
              disabled={!editableUngated}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Field
              id='disable-client-create'
              name='disable-client-create'
              data-test='disable-client-create'
              label={
                <>
                  <span>
                    Block client-side Inquiry creation
                    <HelpIcon>
                      <p>
                        When enabled, Inquiries will not be allowed from client-side flow
                        integrations, and will only be creatable via API or the Persona Dashboard.
                      </p>
                      <br />
                      <p>
                        This lets you control exactly when Inquiries are created instead of allowing
                        end users to freely create Inquiries.
                      </p>
                    </HelpIcon>
                  </span>

                  <Paragraph>
                    For more information, see our documentation on{' '}
                    <a
                      href='https://docs.withpersona.com/docs/creating-inquiries#creating-inquiries-via-api'
                      target='_blank'
                      rel='noreferrer'
                    >
                      Creating Inquiries via API
                    </a>
                    .
                  </Paragraph>
                </>
              }
              labelOn='Blocking enabled'
              labelOff='Blocking disabled'
              component={ToggleSliderField}
              disabled={!editableUngated}
            />
          </FieldWrapper>
        </Card.Body>
      </Card>,
    );

    return configCards;
  }, [
    basePath,
    editableUngated,
    hasAccessForBasicTier,
    steps,
    validationErrors,
    vtmplVersionMap,
    dispatch,
    fieldSchemaOptions,
    selectedSupportedLocales,
    supportedLocales,
    confirmationActive,
  ]);

  return (
    <RightColumn ref={rightColumnRef}>
      {title && (
        <>
          <Spacer height={3} />
          <Heading size='sm'>{title}</Heading>
        </>
      )}
      <Spacer height={3} />
      {configCards}
    </RightColumn>
  );
};

export default FormSectionGeneralConfig;

interface AllowedHostsListFieldProps
  extends WrappedFieldProps,
    ComponentProps<typeof AllowedHostsList> {
  label: string;
}

const AllowedHostsListField = ({
  label,
  input,
  meta: { error, submitFailed, touched },
  disabled,
}: AllowedHostsListFieldProps) => {
  const hasError = !!(error && (submitFailed || touched));

  const addAllowedHost = (values: { host: string }): void => {
    input.onChange((input.value ?? []).concat(values.host));
  };
  const removeAllowedHost = (hostIndex: number): void => {
    const newValue = ((input.value ?? []) as string[]).filter((_, index) => index !== hostIndex);
    input.onChange(newValue);
  };

  return (
    <>
      {label && <FormControlLabel htmlFor='host'>{label}</FormControlLabel>}
      <AllowedHostsList
        // account for default input.value of ''
        allowedHosts={input.value || []}
        addAllowedHost={addAllowedHost}
        removeAllowedHost={removeAllowedHost}
        disabled={disabled}
      />
      {hasError && <FormError>{error}</FormError>}
    </>
  );
};
